import React from "react";
import moment from "moment-timezone";
import { localTimeCandidates, normalizeShiftBreaks } from "./time";
import { EditorInput, Button, ScheduleEditorTheme } from "./ScheduleEditorUI";
import { createBlock } from "../../api/Scheduling/blocks";
import { blockOverlapFromError } from "./BlockOverlapWarning";

export const defaultBreak = { startTime: "12:00", endTime: "12:30" };

export async function createBreakBlocks({ organizationRef, scheduleId, ranges, confirmOverlap }) {
  for (const range of ranges) {
    const body = {
      organizationRef,
      scheduleId,
      startsAt: range.startsAt,
      endsAt: range.endsAt,
      reasonCode: "BREAK",
    };
    let confirmationToken;
    for (;;) {
      try {
        await createBlock({ ...body, ...(confirmationToken ? { confirmationToken } : {}) });
        break;
      } catch (error) {
        const overlap = blockOverlapFromError(error);
        if (!overlap || !confirmOverlap) throw error;
        if (!await confirmOverlap(overlap)) throw new Error("Break creation cancelled.");
        confirmationToken = overlap.confirmationToken;
      }
    }
  }
}

export default function ShiftBreaksFields({
  breaks = [],
  onChange,
  shift,
  localDate,
  timezoneName,
  removeAriaLabel = (index) => `Remove break ${index + 1}`,
}) {
  return (
    <ScheduleEditorTheme>
      <div className="schedule-breaks">
        <div className="schedule-section-title">
          <h4>Breaks</h4>
          <span className="schedule-help">Optional · unavailable for appointments</span>
        </div>
        {breaks.map((pause, pauseIndex) => {
          const interval = shift && normalizeShiftBreaks({ ...shift, breaks: [pause] }).intervals?.[0];
          const repeated = interval && localDate && timezoneName && [
            [interval.start, pause.startTime], [interval.end, pause.endTime],
          ].some(([minute, time]) => localTimeCandidates(moment.utc(localDate).add(Math.floor(minute / 1440), "days").format("YYYY-MM-DD"), time, timezoneName).length > 1);
          return (
          <div className="schedule-time-range" key={pauseIndex}>
            <label>
              Break start
              <EditorInput
                aria-label="Break start"
                type="time"
                required
                value={pause.startTime}
                onChange={(event) =>
                  onChange(breaks.map((item, index) => (index === pauseIndex ? { ...item, startTime: event.target.value, disambiguation: "reject" } : item)))
                }
              />
              {interval?.start >= 1440 && <small>Following day</small>}
            </label>
            <span aria-hidden="true">to</span>
            <label>
              Break end
              <EditorInput
                aria-label="Break end"
                type="time"
                required
                value={pause.endTime}
                onChange={(event) =>
                  onChange(breaks.map((item, index) => (index === pauseIndex ? { ...item, endTime: event.target.value, disambiguation: "reject" } : item)))
                }
              />
              {interval?.end >= 1440 && <small>Following day</small>}
            </label>
            {repeated && <label>Repeated break hour
              <select aria-label={`Repeated break hour ${pauseIndex + 1}`} value={pause.disambiguation || "reject"}
                onChange={event => onChange(breaks.map((item, index) => index === pauseIndex ? { ...item, disambiguation: event.target.value } : item))}>
                <option value="reject">Choose an occurrence</option>
                <option value="earlier">First occurrence</option>
                <option value="later">Second occurrence</option>
              </select>
            </label>}
            <Button
              variant="text"
              type="button"
              className="schedule-text-button"
              aria-label={removeAriaLabel(pauseIndex)}
              onClick={() => onChange(breaks.filter((_, index) => index !== pauseIndex))}
            >
              Remove
            </Button>
          </div>
        ); })}
        <Button
          variant="text"
          type="button"
          className="schedule-text-button"
          onClick={() => onChange([...breaks, { ...defaultBreak }])}
        >
          + Add a break
        </Button>
      </div>
    </ScheduleEditorTheme>
  );
}
